import React from 'react'
import { Avatar } from '@mui/material'
import DataUnavailable from '@/components/DataUnavailable'
import Loader from '@/components/Loader'
import CustomPageNavigator from '@/components/Pagination'
import { AdminRandomCheckInInfo } from '@/services/types/APIResponseType'
import { stringAvatar } from '@/utils'
import { ReactComponent as TickCircleIcon } from '@/assets/RandomCheckIns/tick-circle.svg'
import close_circle_img from '@/assets/RandomCheckIns/close-circle.svg'
import download_random_check_in_icon from '@/assets/RandomCheckIns/download-check-ins.svg'
import { createSearchParams, useNavigate } from 'react-router-dom'
import Path from '@/router/localRoutes'

interface CheckInTablePropsType {
  adminCheckInTableData: AdminRandomCheckInInfo[]
  currentPage: number
  isLoading: boolean
  itemsPerPage: number
  totalCount: number
  setCurrentPage: (page: number) => void
}

const CheckInDetailsTable: React.FC<CheckInTablePropsType> = ({
  adminCheckInTableData,
  currentPage,
  isLoading,
  itemsPerPage,
  setCurrentPage,
  totalCount,
}) => {
  const navigate = useNavigate()

  const handleViewNavigate = (consumerID: string) => {
    navigate({
      pathname: Path.RegisteredConsumerDetails,
      search: createSearchParams({
        from: 'random-check-ins',
        consumerID,
      }).toString(),
    })
  }

  const handleDownloadCsv = (item: AdminRandomCheckInInfo) => {
    const formattedData = item.consumersubmission.map((checkIn) => ({
      User_Name: `${item.profile.firstName} ${item.profile.lastName}`,
      CheckIn_ID: checkIn._id,
      CheckIn_Date: checkIn.checkIn_initiated_date,
      CheckIn_Time: checkIn.checkIn_initiated_time,
      Authentication: checkIn.authentication,
      Govt_ID_Uploaded: checkIn.uploadedGovtId,
      Country: checkIn.country || 'N/A',
      City: checkIn.city || 'N/A',
      State: checkIn.state || 'N/A',
      Address:
        ` ${checkIn.sublocality_level_1} ${checkIn.sublocality_level_2} ` ||
        'N/A',
      street_number: checkIn.street_number || 'N/A',
      route: checkIn.route || 'N/A',
      neighborhood: checkIn.neighborhood || 'N/A',
      CheckIn_Initiated_By: checkIn.checkIn_initiated_by,
    }))

    const csvHeaders = [
      'User_Name',
      'CheckIn_ID',
      'CheckIn_Date',
      'CheckIn_Time',
      'Authentication',
      'Govt_ID_Uploaded',
      'Country',
      'City',
      'State',
      'Address',
      'street_number',
      'route',
      'neighborhood',
      'CheckIn_Initiated_By',
    ]

    const csvRows = [
      csvHeaders.join(','),
      ...formattedData.map((row: Record<string, string>) =>
        csvHeaders
          .map((fieldName) =>
            JSON.stringify(row[fieldName], (key, value) => value ?? ''),
          )
          .join(','),
      ),
    ]

    const csv = csvRows.join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })

    const link = document.createElement('a')
    const fileName = `${item.profile.firstName}_${item.profile.lastName}_CheckInData.csv`
    link.href = URL.createObjectURL(blob)
    link.download = fileName

    link.click()
  }

  if (isLoading) {
    return <Loader />
  }
  return (
    <div>
      {adminCheckInTableData.length > 0 ? (
        <>
          <div className="responsive-table-scroll mt-8">
            <div className="bg-grad before:rounded-[21px] w-full">
              <table className="table-container reg-consumer-table !mt-0">
                <thead>
                  <tr content="start" className="text-left row-bg1">
                    <th className="border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                      Name
                    </th>
                    <th className="!text-center border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                      Check-ins
                    </th>
                    <th className="border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                      Location
                    </th>
                    <th className="border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                      Verified
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {adminCheckInTableData?.map((item, idx) => (
                    <tr
                      key={idx}
                      className={idx % 2 === 0 ? 'rowbg2' : 'row-bg1'}
                    >
                      <td className="text-left border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                        <div
                          className="profile-cell cursor-pointer"
                          onClick={() => handleViewNavigate(item?._id)}
                        >
                          {item?.profile.profilePic ? (
                            <img
                              alt="Profile"
                              src={item.profile.profilePic}
                              className="consumer-avatar"
                            />
                          ) : (
                            <Avatar
                              className="!bg-[#365F91] mr-4"
                              {...stringAvatar(
                                `${item.profile.firstName} ${item.profile.lastName}`,
                              )}
                            />
                          )}
                          &nbsp;
                          {item.profile.firstName}&nbsp;{item.profile.lastName}
                        </div>
                      </td>
                      <td className="text-left border border-[#d8e4fb33] border-r border-b-[0] border-t-[0] gap-3">
                        <div className="flex justify-around gap-3">
                          {item.consumersubmission &&
                            item.consumersubmission[0] &&
                            (item.consumersubmission[0].authentication ===
                            'Fail' ? (
                              <img
                                src={close_circle_img}
                                alt="check in failed"
                                className="h-[24px] w-[24px]"
                              />
                            ) : (
                              <TickCircleIcon
                                className={`${item.consumersubmission[0].authentication === 'Pending' && '[&>path]:fill-[#606369]'} h-[24px] w-[24px]`}
                              />
                            ))}
                        </div>
                      </td>
                      <td className="text-left border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                        {item.consumersubmission && item.consumersubmission[0]
                          ? [
                              item.consumersubmission[0].street_number,
                              item.consumersubmission[0].route,
                              item.consumersubmission[0].neighborhood,
                              item.consumersubmission[0].sublocality_level_1,
                              item.consumersubmission[0].city,
                              item.consumersubmission[0].state,
                              item.consumersubmission[0].country,
                            ]
                              .filter(Boolean)
                              .join(', ')
                          : null}
                      </td>
                      <td className="border border-[#d8e4fb33] border-r border-b-[0] border-t-[0]">
                        {item.consumersubmission &&
                          item.consumersubmission[0] &&
                          (item.consumersubmission[0].authentication ===
                          'Fail' ? (
                            <img
                              src={close_circle_img}
                              alt="Not verified"
                              className="h-[30px] w-[30px]"
                            />
                          ) : (
                            <TickCircleIcon
                              className={`${item.consumersubmission[0].authentication === 'Pending' && '[&>path]:fill-[#606369]'} h-[30px] w-[30px]`}
                            />
                          ))}
                      </td>
                      <td className="flex justify-center items-center">
                        <img
                          src={download_random_check_in_icon}
                          alt="Check-in records download"
                          className="cursor-pointer"
                          onClick={() => handleDownloadCsv(item)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <CustomPageNavigator
            currentPage={currentPage}
            limitPerPage={itemsPerPage}
            totalItemCount={totalCount}
            setCurrentPage={setCurrentPage}
          />
        </>
      ) : (
        <DataUnavailable description="No record found" />
      )}
    </div>
  )
}

export default CheckInDetailsTable
